import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { TabTitle } from "../../Utils/GeneralFunctions";
import NoUser from "../../ErrorPages/NoUser";
import track, { TRACKING_TYPES } from "../../Utils/SupabaseServices/Track";
import FeedControls from "../../components/FeedPage/FeedControls/FeedControls";
import { useLoadCompletePage } from "../../Utils/customHooks/useLoadCompletePage";
import { useLoadRenderComponent } from "../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import FrontendSkeleton from "../../components/skeleton/Views/FrontendSkeleton";
import { useLoadProfileSetup } from "../../Utils/customHooks/useLoadProfileSetup";
import StayUpToDateSkeleton from "../DashboardView/StayUpToDate/StayUpToDateComponents/StayUpToDateSkeleton";
import SlidingSidebar from "../../components/SlidingSidebar/SlidingSidebar";
import Sidebar from "../DashboardView/Dashboard/Sidebar";
import { useSidebarState } from "../../context/SidebarStateContext";
import { useSidebar } from "../../context/SidebarContext";
import { useLoadUser } from "../../Utils/customHooks/useLoadUser";
import { fetchUser } from "../../Utils/SupabaseServices/UserDatabase";
import { message } from "antd";
import { FaLink } from "react-icons/fa";
import { copyEmbedCode } from "../DashboardView/EmbedSearch/EmbedSearch";
import { BsSearch } from "react-icons/bs";
import CreatorLandingProfile from "../../components/CreatorProfile/CreatorLandingProfile";
import { supabase } from "../../config/supabaseClient";
import CreatorProfile from "../../components/CreatorProfile/creatorProfile";
import 'iframe-resizer/js/iframeResizer.contentWindow.min.js';

// Add TypeScript interface for window.parentIFrame
declare global {
  interface Window {
    parentIFrame?: {
      autoResize: boolean;
      size: (height: number) => void;
      setHeight?: (height: number) => void;
    };
  }
}

const EmbeddedView = () => {
  const page = "preview";
  const [showSearch, setShowSearch] = useState(false);
  const [showSearchLoading, setShowSearchLoading] = useState(false);
  const [removeSearchLoading, setRemoveSearchLoading] = useState(false);
  const location = useLocation();
  const param = useParams();
  const lowerUsername = param?.username?.toLowerCase();
  const { search } = location;
  const isFollowerView = location.pathname.includes("preview");
  const queryParams = new URLSearchParams(search);
  const [isNavigating, setIsNavigating] = useState(false);

  const utmSource = queryParams.get("utm_source");
  if (utmSource) {
    track(location.pathname, TRACKING_TYPES.PAGE, {
      pathname: location.pathname,
      details: { source: lowerUsername },
      // user_id: "",
    });
  }

  const indexPages: any = location?.state?.indexPages;

  if (location.state) {
    TabTitle("Embedded View • Bubble");
  } else {
    TabTitle(`${lowerUsername} • Search Bubble `);
  }
  const { isLoading: profileSetupLoading, data: profileSetupData } =
    useLoadProfileSetup(lowerUsername, page);
  const {
    isLoading: userLoading,
    data: userData,
    refetch,
  }: any = useLoadUser(lowerUsername);
  const { isLoading, data, error }: any = useLoadCompletePage(
    lowerUsername,
    page,
    profileSetupData?.combinedRes?.userData?.user_id,
    profileSetupData?.combinedRes?.feedControls,
    profileSetupData?.combinedRes?.instagram_links,
    profileSetupData?.combinedRes?.userData?.is_fillout_on,
    "embed-search",
    profileSetupData?.combinedRes?.is_embed_search
  );
  const {
    handleClick,
    isEmail,
    isName,
    isPhone,
    isPhoneEmpty,
    tab,
    isEmailVerified,
    option,
    setIsEmail,
    setIsName,
    setIsPhone,
    setIsPhoneEmpty,
    setTab,
    setIsEmailVerified,
  } = useSidebarState();
  const { isOpen, closeSidebar } = useSidebar();
  const [contentShifted, setContentShifted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Add new state for embed configuration
  const [configLoading, setConfigLoading] = useState(true);
  const [embedConfig, setEmbedConfig] = useState({
    showProfilePicture: false,
    showNameAndDesc: false,
    showSocialLinks: false,
  });

  // Add a ref for the container
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to send height to parent
  const sendHeight = useCallback(() => {
    if (containerRef.current && window.parent !== window) {
      const height = containerRef.current.offsetHeight;
      window.parent.postMessage({ height, type: 'resize' }, '*');
    }
  }, []);

  // Listen for height changes
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      sendHeight();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [sendHeight]);

  // Send initial height after content loads
  useEffect(() => {
    if (!profileSetupLoading && !loading) {
      setTimeout(sendHeight, 100);
    }
  }, [profileSetupLoading, loading, sendHeight]);

  useEffect(() => {
    if (isFollowerView && isOpen) {
      setTimeout(() => {
        setContentShifted(true);
      }, 10);
    } else {
      setContentShifted(false);
    }
  }, [isOpen, isFollowerView]);

  useEffect(() => {
    fetchEmail();
  }, [isEmail, isName]);

  const fetchEmail = useCallback(async () => {
    try {
      const userData = await fetchUser(lowerUsername);
      if (!userData) return;

      const hasEmail = userData.email !== "";
      const hasName = userData.name !== "";

      if (hasEmail && hasName) {
        setIsName(true);
        setIsEmail(true);
        setIsPhone(true);
        setIsEmailVerified(true);
      } else {
        setIsEmail(hasEmail);
        setIsName(hasName);
        handleClick("Account");
        setTab(0);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  }, [lowerUsername]);

  useEffect(() => {
    const route = location.pathname;
    let title: any;
    if (route.includes("preview")) {
      // updateFollowerCount(username);
      title = "Follower";
    } else {
      // updateLiveCount(username);
      title = "Live";
    }
    if (!profileSetupLoading) {
      track(location.pathname, TRACKING_TYPES.PAGE, {
        pathname: location.pathname,
        title: title,
        user_id: profileSetupData?.combinedRes?.userData?.user_id,
      });
      if (param?.id) {
        track(`/${lowerUsername}`, TRACKING_TYPES.PAGE, {
          pathname: `/${lowerUsername}`,
          title: title,
          user_id: profileSetupData?.combinedRes?.userData?.user_id,
        });
      }
    }
  }, [profileSetupLoading, profileSetupData?.combinedRes]);

  const {
    isLoading: StayUpToDateComponentsDataIndividualLoading,
    data: StayUpToDateComponentsDataIndividual,
  } = useLoadRenderComponent(
    profileSetupData?.combinedRes?.userData?.user_id,
    param?.id,
    "embed-search"
  );

  useEffect(() => {
    setRemoveSearchLoading(true);
    const updateSearchBar = () => {
      if (
        profileSetupData?.combinedRes?.feedControls?.checkedType ===
        "Landing Page"
      ) {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());

        if (
          urlSearchParams?.size > 0 &&
          (params.hasOwnProperty("term") || params.hasOwnProperty("from"))
        ) {
          setShowSearch(true);
        } else {
          setShowSearch(false);
        }
      } else {
        setShowSearch(true);
      }
    };
    updateSearchBar();

    const intervalId = setInterval(() => {
      setRemoveSearchLoading(false);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [
    profileSetupData?.combinedRes?.feedControls?.checkedType,
    location.search,
  ]);

  // Add effect to load embed configuration
  useEffect(() => {
    const loadEmbedConfig = async () => {
      setConfigLoading(true);
      if (profileSetupData?.combinedRes?.userData?.user_id) {
        try {
          const { data: user } = await supabase
            .from("user")
            .select("embed_configure")
            .eq("user_id", profileSetupData.combinedRes.userData.user_id)
            .single();

          if (user?.embed_configure) {
            setEmbedConfig({
              showProfilePicture:
                user.embed_configure.showProfilePicture ?? false,
              showNameAndDesc: user.embed_configure.showNameAndDesc ?? false,
              showSocialLinks: user.embed_configure.showSocialLinks ?? false,
            });
          } else {
            // If no configuration exists, keep everything hidden
            setEmbedConfig({
              showProfilePicture: false,
              showNameAndDesc: false,
              showSocialLinks: false,
            });
          }
        } catch (error) {
          console.error("Error loading embed configuration:", error);
          // On error, keep everything hidden
          setEmbedConfig({
            showProfilePicture: false,
            showNameAndDesc: false,
            showSocialLinks: false,
          });
        } finally {
          setConfigLoading(false);
        }
      }
    };

    loadEmbedConfig();
  }, [profileSetupData?.combinedRes?.userData?.user_id]);
  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isNavigating]);

  useEffect(() => {
    // Reset navigation state when location changes
    setIsNavigating(true);
  }, [location.pathname]);

  useEffect(() => {
    // Initialize as iframe content
    if (!window.parentIFrame) {
      window.parentIFrame = {
        autoResize: true,
        size: (height: number) => {
          window.parentIFrame?.setHeight?.(height);
        },
      };
    }
  }, []);

  // Function to trigger resize
  const triggerResize = useCallback(() => {
    if (window.parentIFrame) {
      // Pass current height as argument
      const height = containerRef.current?.offsetHeight || 0;
      window.parentIFrame.size(height);
    }
  }, []);

  // Listen for content changes that might affect height
  useEffect(() => {
    if (!profileSetupLoading && !loading) {
      // Give time for content to render
      setTimeout(triggerResize, 100);
    }
  }, [
    profileSetupLoading, 
    loading, 
    triggerResize,
    data,
    profileSetupData,
    showSearch,
    contentShifted
  ]);

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      triggerResize();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [triggerResize]);

  return (
    <div ref={containerRef} className="embed-view-container" style={{ width: '100%' }}>
      {profileSetupLoading || loading ? (
        <FrontendSkeleton from="embed-search" />
      ) : (
        <>
          <div className="embed-search-wrapper">
            <div className={` ${isFollowerView ? "dashboard-layout" : ""}`}>
              {isFollowerView && (
                <SlidingSidebar
                  type={
                    location?.pathname.includes("preview")
                      ? "preview"
                      : "dashboard"
                  }
                >
                  <Sidebar
                    username={lowerUsername}
                    pictureUrl={userData?.picture}
                    email_verify={userData?.email_verify}
                  />
                </SlidingSidebar>
              )}

              <div className={`${contentShifted ? "content-shifted" : ""}`}>
                {error ? (
                  <NoUser />
                ) : profileSetupData?.combinedRes?.userData?.name &&
                  profileSetupData?.combinedRes?.userData?.user_id &&
                  lowerUsername ? (
                  <>
                    <div className="">
                      {!configLoading &&
                        (embedConfig.showProfilePicture ||
                          embedConfig.showNameAndDesc ||
                          embedConfig.showSocialLinks) && (
                          <CreatorProfile
                            userDetails={
                              profileSetupData?.combinedRes?.userData
                            }
                            showIcons={
                              profileSetupData?.combinedRes?.feedControls
                                ?.showIcons
                            }
                            liveView={
                              location.pathname.includes("/preview")
                                ? false
                                : true
                            }
                            username={lowerUsername}
                            showSocialLinks={embedConfig.showSocialLinks}
                            socialLinks={
                              embedConfig.showSocialLinks
                                ? profileSetupData?.combinedRes?.userData
                                : null
                            }
                            description={
                              embedConfig.showNameAndDesc
                                ? profileSetupData?.combinedRes?.description
                                : ""
                            }
                            setShowSearch={setShowSearch}
                            showSearch={showSearch}
                            setShowSearchLoading={setShowSearchLoading}
                            desiredOrder={
                              embedConfig.showSocialLinks
                                ? profileSetupData?.combinedRes?.userData?.social_links_order?.split(
                                    ","
                                  )
                                : []
                            }
                            showSearchbar={true}
                            loading={loading}
                            showProfilePicture={embedConfig.showProfilePicture}
                            showNameAndDesc={embedConfig.showNameAndDesc}
                            from="embed-search"

                          />
                        )}
                    </div>
                        {/* <FeedControls
                          indexPages={indexPages}
                          userDetails={profileSetupData?.combinedRes?.userData}
                          username={lowerUsername}
                          page="preview"
                          name={profileSetupData?.combinedRes?.userData?.name}
                          userId={profileSetupData?.combinedRes?.userData?.user_id}
                          data={data?.combinedRes}
                          profileData={profileSetupData?.combinedRes}
                          hashtagFilters={data?.hashtagFilterArray}
                          avgWords={data?.combinedRes?.userData?.avg_words}
                          liveView={false}
                          StayUpToDateComponentsDataIndividual={
                            StayUpToDateComponentsDataIndividual
                          }
                          StayUpToDateComponentsDataIndividualLoading={
                            StayUpToDateComponentsDataIndividualLoading
                          }
                          showSearch={true}
                          showSearchLoading={showSearchLoading}
                          setShowSearchLoading={setShowSearchLoading}
                          setShowSearch={setShowSearch}
                          StayUpToDateComponentsLoading={isLoading}
                          StayUpToDateComponents={
                            data?.combinedRes?.stayUpToDateComponentsData
                          }
                          removeSearchLoading={removeSearchLoading}
                          setRemoveSearchLoading={setRemoveSearchLoading}
                          from="embed-search"
                          isLandingPage={false}
                      refetchUser={refetch}
                      refetchUserDetail={refetch}
                      creatorData={profileSetupData}
                      isLoading={isLoading}
                      brand_color={
                        profileSetupData?.combinedRes?.userData?.brand_color
                      }
                      isFilloutOn={
                        profileSetupData?.combinedRes?.userData?.is_fillout_on
                      }
                    /> */}

                    <FeedControls
                      indexPages={indexPages}
                      userDetails={profileSetupData?.combinedRes?.userData}
                      username={lowerUsername}
                      page="preview"
                      name={profileSetupData?.combinedRes?.userData?.name}
                      userId={profileSetupData?.combinedRes?.userData?.user_id}
                      data={data?.combinedRes}
                      profileData={profileSetupData?.combinedRes}
                      hashtagFilters={data?.hashtagFilterArray}
                      avgWords={data?.combinedRes?.userData?.avg_words}
                      liveView={
                        location.pathname.includes("/preview") ? false : true
                      }
                      StayUpToDateComponentsDataIndividual={
                        StayUpToDateComponentsDataIndividual
                      }
                      StayUpToDateComponentsDataIndividualLoading={
                        StayUpToDateComponentsDataIndividualLoading
                      }
                      showSearch={showSearch}
                      showSearchLoading={showSearchLoading}
                      setShowSearchLoading={setShowSearchLoading}
                      setShowSearch={setShowSearch}
                      StayUpToDateComponentsLoading={isLoading}
                      StayUpToDateComponents={
                        data?.combinedRes?.stayUpToDateComponentsData
                      }
                      from="embed-search"
                      isNavigating={isNavigating}
                      setIsNavigating={setIsNavigating}
                      // removeSearchLoading={removeSearchLoading}
                      // setRemoveSearchLoading={setRemoveSearchLoading}
                    />
                  </>
                ) : (
                  <NoUser />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmbeddedView;
